import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Send, CheckCircle2, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "SERP";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", {
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        country: country.trim(),
        message: message.trim(),
        source: "contact",
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Message failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <SiteHeader variant="public" />

      <main className="max-w-[1080px] mx-auto px-6 py-16">
        {sent ? (
          <div className="bg-white border border-[#12B76A] p-10 text-center" data-testid="contact-success">
            <CheckCircle2 size={40} className="text-[#12B76A] mx-auto mb-4" />
            <h1 className="font-heading font-black text-3xl text-[#1A202C] mb-2">Message received.</h1>
            <p className="text-[#4A5568] max-w-md mx-auto">
              Thanks {name.split(" ")[0]}. Our team will reach out within one business day at{" "}
              <strong className="text-[#1A202C]">{email}</strong>.
            </p>
            <Link to="/" className="btn-ghost mt-8 inline-flex" data-testid="contact-back-home">
              Back to home
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <aside className="md:col-span-1 space-y-6" data-testid="contact-info">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#1C1C1C] font-semibold mb-3">Talk to us</div>
                <h1 className="font-heading font-black text-3xl md:text-4xl text-[#1A202C] tracking-tight mb-4">
                  Talk to the SERP team.
                </h1>
                <p className="text-[#4A5568] leading-relaxed">
                  Whether you're evaluating SERP for a single site or a portfolio of mines,
                  we'll walk you through it. Expect a response within one business day.
                </p>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#DDE3ED]">
                <div className="flex items-start gap-3" data-testid="contact-detail-email">
                  <Mail size={16} className="text-[#1C1C1C] mt-1 flex-shrink-0" />
                  <div>
                    <div className="overline mb-1">Email</div>
                    <a href="mailto:info@sresources.co" className="text-[#1A202C] font-semibold hover:text-[#1C1C1C]">
                      info@sresources.co
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="contact-detail-phone">
                  <Phone size={16} className="text-[#1C1C1C] mt-1 flex-shrink-0" />
                  <div>
                    <div className="overline mb-1">Phone</div>
                    <a href="tel:+918079989999" className="text-[#1A202C] font-semibold hover:text-[#1C1C1C]">
                      +91 80799 89999
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="contact-detail-address">
                  <MapPin size={16} className="text-[#1C1C1C] mt-1 flex-shrink-0" />
                  <div>
                    <div className="overline mb-1">Office</div>
                    <div className="text-[#1A202C] font-semibold">
                      Bhubaneswar,<br />Odisha, India
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            <form
              onSubmit={submit}
              className="md:col-span-2 bg-white border border-[#DDE3ED] p-8 grid grid-cols-1 sm:grid-cols-2 gap-5"
              data-testid="contact-form"
            >
              <div>
                <label className="label-field">Full name *</label>
                <input
                  data-testid="contact-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-clean"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  required
                />
              </div>
              <div>
                <label className="label-field">Work email *</label>
                <input
                  data-testid="contact-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  className="input-clean"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  required
                />
              </div>
              <div>
                <label className="label-field">Company</label>
                <input
                  data-testid="contact-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="input-clean"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  placeholder="e.g. Shanvi Resources"
                />
              </div>
              <div>
                <label className="label-field">Country</label>
                <input
                  data-testid="contact-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="input-clean"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  placeholder="e.g. India"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label-field">How can we help?</label>
                <textarea
                  data-testid="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  className="input-clean"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  placeholder="Tell us about your operation — commodity, sites, team size — and what you'd like to see in a demo."
                />
              </div>
              <div className="sm:col-span-2 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-[#8896A8]">
                  By submitting you agree to our{" "}
                  <Link to="/terms" className="underline">Terms</Link> &{" "}
                  <Link to="/privacy" className="underline">Privacy</Link>.
                </p>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary"
                  data-testid="contact-submit"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send message
                </button>
              </div>
            </form>
          </div>
        )}
      </main>

      <footer className="border-t border-[#DDE3ED] py-8 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-xs text-[#4A5568]">
          <div>SERP · Shanvi Enterprise Resource Planning · a Shanvi Resources product</div>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-[#1A202C]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#1A202C]">Privacy</Link>
            <Link to="/contact" className="hover:text-[#1A202C]">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
